import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Result, Spin } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  getInsurerSyncPreview,
  getOrderDetail,
  syncInsurerOrder,
} from "@ifa/utils";
import type { InsurerSyncPreviewVO, OrderDetail } from "@ifa/utils";
import CustomButton from "../../components/CustomButton";
import { useIOSConfirm } from "../../hooks";
import styles from "./OrderSuccess.module.css";

interface InsurerSyncSummary {
  lastPushTime: string;
  pushCount: string;
  orderNo: string;
  insurerName: string;
  brokerCompanyName: string;
  brokerCompanyCode: string;
  advisorName: string;
  advisorInsuranceCode: string;
  hasAdvisorInsuranceCode: boolean;
  policyholderName: string;
  productName: string;
}

const formatNullableText = (value?: string | number | null) => {
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

const formatDateTimeValue = (value?: string | null) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : value;
};

const formatPushCountValue = (value?: number | string | null) => {
  const text = formatNullableText(value);
  if (!text) return "0次";
  return text.endsWith("次") ? text : `${text}次`;
};

const formatChineseEnglishName = (
  chineseName?: string | null,
  englishName?: string | null,
) => {
  const chinese = chineseName?.trim();
  const english = englishName?.trim();
  if (chinese && english) return `${chinese}(${english})`;
  return chinese || english || undefined;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message.trim()
  ) {
    return error.message;
  }
  return fallback;
};

const getInsurerSyncSummary = (
  preview: InsurerSyncPreviewVO,
): InsurerSyncSummary => {
  const advisorName = formatNullableText(preview.signAdvisorName) || "-";
  const advisorEmpNo = formatNullableText(preview.signAdvisorEmpNo);
  const advisorInsuranceCode = formatNullableText(
    preview.signAdvisorInsurerCode,
  );

  return {
    lastPushTime: formatDateTimeValue(preview.latestSyncTime),
    pushCount: formatPushCountValue(preview.syncCount),
    orderNo: formatNullableText(preview.orderNo) || "-",
    insurerName: formatNullableText(preview.insuranceCompanyName) || "-",
    brokerCompanyName: formatNullableText(preview.brokerCompanyName) || "-",
    brokerCompanyCode: formatNullableText(preview.brokerCompanyCode) || "-",
    advisorName:
      advisorEmpNo && advisorName !== "-"
        ? `${advisorName}(${advisorEmpNo})`
        : advisorName,
    advisorInsuranceCode: advisorInsuranceCode || "-",
    hasAdvisorInsuranceCode: !!advisorInsuranceCode,
    policyholderName:
      formatChineseEnglishName(
        preview.policyholderChineseName,
        preview.policyholderEnglishName,
      ) || "-",
    productName: formatNullableText(preview.productName) || "-",
  };
};

export const OrderSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const { confirm } = useIOSConfirm();
  const orderStatus = (location.state as any)?.orderStatus;
  const orderAuditStatus = (location.state as any)?.orderAuditStatus;
  const [insurerSyncSupported, setInsurerSyncSupported] = useState(false);
  const [insurerSyncModalOpen, setInsurerSyncModalOpen] = useState(false);
  const [insurerSyncPreview, setInsurerSyncPreview] =
    useState<InsurerSyncPreviewVO | null>(null);
  const [insurerSyncPreviewLoading, setInsurerSyncPreviewLoading] =
    useState(false);
  const [insurerSyncSubmitting, setInsurerSyncSubmitting] = useState(false);
  const insurerSyncPreviewRequestIdRef = useRef(0);

  const currentOrderId = useMemo(() => {
    if (!orderId) return null;
    const parsedOrderId = Number(orderId);
    return Number.isFinite(parsedOrderId) && parsedOrderId > 0
      ? parsedOrderId
      : null;
  }, [orderId]);

  useEffect(() => {
    if (!currentOrderId) return;

    let cancelled = false;
    getOrderDetail<OrderDetail>(currentOrderId)
      .then((detail) => {
        if (cancelled) return;
        setInsurerSyncSupported(detail.insurerSyncSupported === true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Fetch order detail failed:", error);
        setInsurerSyncSupported(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentOrderId]);

  const handleGoToRiskAssessment = () => {
    navigate(`/orders/${orderId}/risk`);
  };

  const handleBackToOrderList = () => {
    navigate("/orders");
  };

  const handleViewOrderDetails = () => {
    navigate(`/orders/${orderId}`, {
      state: {
        readOnly: true,
        orderStatus,
        orderAuditStatus,
      },
    });
  };

  const handleOpenInsurerSyncModal = async () => {
    if (!currentOrderId) {
      confirm({
        title: "推送保司",
        content: "缺少訂單ID，暫時無法推送保司。",
        okText: "確認",
        showCancel: false,
      });
      return;
    }

    const requestId = insurerSyncPreviewRequestIdRef.current + 1;
    insurerSyncPreviewRequestIdRef.current = requestId;
    setInsurerSyncModalOpen(true);
    setInsurerSyncPreview(null);
    setInsurerSyncPreviewLoading(true);

    try {
      const preview = await getInsurerSyncPreview(currentOrderId);
      if (insurerSyncPreviewRequestIdRef.current === requestId) {
        setInsurerSyncPreview(preview || null);
      }
    } catch (error) {
      console.error("Fetch insurer sync preview failed:", error);
      if (insurerSyncPreviewRequestIdRef.current === requestId) {
        setInsurerSyncModalOpen(false);
        setInsurerSyncPreview(null);
        confirm({
          title: "推送保司",
          content: getErrorMessage(
            error,
            "推送保司預覽資料加載失敗，請稍後再試。",
          ),
          okText: "確認",
          showCancel: false,
        });
      }
    } finally {
      if (insurerSyncPreviewRequestIdRef.current === requestId) {
        setInsurerSyncPreviewLoading(false);
      }
    }
  };

  const handleCloseInsurerSyncModal = () => {
    if (insurerSyncSubmitting) return;
    insurerSyncPreviewRequestIdRef.current += 1;
    setInsurerSyncModalOpen(false);
    setInsurerSyncPreview(null);
    setInsurerSyncPreviewLoading(false);
  };

  const handleConfirmInsurerSync = async () => {
    if (
      !currentOrderId ||
      insurerSyncSubmitting ||
      insurerSyncPreviewLoading ||
      !formatNullableText(insurerSyncPreview?.signAdvisorInsurerCode)
    ) {
      return;
    }

    try {
      setInsurerSyncSubmitting(true);
      await syncInsurerOrder(currentOrderId);
      insurerSyncPreviewRequestIdRef.current += 1;
      setInsurerSyncModalOpen(false);
      setInsurerSyncPreview(null);
      confirm({
        title: "推送保司",
        content:
          "推送保司成功。請憑簽單業務代表保司Code登錄保司系統，完成後續投保流程。",
        okText: "確認",
        showCancel: false,
      });
    } catch (error) {
      console.error("Sync insurer order failed:", error);
      confirm({
        title: "推送保司",
        content: getErrorMessage(error, "推送保司失敗，請稍後再試。"),
        okText: "確認",
        showCancel: false,
      });
    } finally {
      setInsurerSyncSubmitting(false);
    }
  };

  const insurerSyncSummary = insurerSyncPreview
    ? getInsurerSyncSummary(insurerSyncPreview)
    : null;

  return (
    <div className={styles.container}>
      <Result
        status="success"
        title="訂單簽署檔案完成!"
        subTitle="您現在可以"
        extra={[
          <CustomButton
            key="risk"
            height={40}
            fontSize={16}
            bgColor="var(--theme-primary)"
            color="#fff"
            onClick={handleGoToRiskAssessment}
            block
            className={styles.primaryButton}
          >
            去填寫風險評估問卷
          </CustomButton>,
          insurerSyncSupported && (
            <CustomButton
              key="insurerSync"
              height={40}
              fontSize={16}
              bgColor="#fff"
              color="var(--theme-primary)"
              style={{ border: "1px solid var(--theme-primary)" }}
              onClick={() => {
                void handleOpenInsurerSyncModal();
              }}
              block
              className={styles.insurerSyncButton}
            >
              推送保司
            </CustomButton>
          ),
          <div key="actions" className={styles.actionButtons}>
            <CustomButton
              height={40}
              fontSize={16}
              bgColor="#fff"
              color="#666"
              style={{ border: "1px solid #d9d9d9" }}
              onClick={handleBackToOrderList}
            >
              返回訂單紀錄
            </CustomButton>
            <CustomButton
              height={40}
              fontSize={16}
              bgColor="#fff"
              color="#666"
              style={{ border: "1px solid #d9d9d9" }}
              onClick={handleViewOrderDetails}
            >
              查看訂單詳情
            </CustomButton>
          </div>,
        ]}
      />
      <Modal
        title="推送保司"
        open={insurerSyncModalOpen}
        onCancel={handleCloseInsurerSyncModal}
        closable={!insurerSyncSubmitting}
        maskClosable={!insurerSyncSubmitting}
        styles={{
          container: {
            padding: "0",
          },
          header: {
            borderBottom: "1px solid #E2E8F0",
            padding: " 16px 0",
            fontSize: "16px",
            fontWeight: 500,
            color: "#09090B",
            margin: "0 24px",
          },
          body: {
            padding: "16px 24px",
          },
          footer: {
            borderTop: "1px solid #E2E8F0",
            padding: "14px 24px",
          },
        }}
        footer={
          <div className={styles.insurerSyncFooter}>
            <CustomButton
              height={40}
              fontSize={18}
              bgColor="#fff"
              color="#666"
              style={{ border: "1px solid #d9d9d9", padding: "0 24px" }}
              onClick={handleCloseInsurerSyncModal}
              disabled={insurerSyncSubmitting}
            >
              取消
            </CustomButton>
            <CustomButton
              height={40}
              fontSize={18}
              bgColor="var(--theme-primary)"
              color="#fff"
              style={{ padding: "0 24px" }}
              onClick={handleConfirmInsurerSync}
              loading={insurerSyncSubmitting}
              disabled={
                insurerSyncPreviewLoading ||
                !insurerSyncSummary?.hasAdvisorInsuranceCode
              }
            >
              確認推送
            </CustomButton>
          </div>
        }
        width={900}
        className={styles.insurerSyncModal}
        centered
      >
        {insurerSyncPreviewLoading ? (
          <div className={styles.insurerSyncLoading}>
            <Spin tip="加載推送資料⋯⋯" />
          </div>
        ) : insurerSyncSummary ? (
          <div className={styles.insurerSyncContent}>
            <div className={styles.insurerSyncMeta}>
              <span>上次推送時間：</span>
              <strong>{insurerSyncSummary.lastPushTime}</strong>
              <span>，總推送次數：</span>
              <strong>{insurerSyncSummary.pushCount}</strong>
            </div>
            <p className={styles.insurerSyncDesc}>
              系統將把該訂單數據推送至保險公司後台，請核對以下推送數據後點擊“確認推送”按鈕
            </p>
            <div className={styles.insurerSyncTable}>
              <div className={styles.insurerSyncLabel}>系統訂單號</div>
              <div className={styles.insurerSyncValue}>
                {insurerSyncSummary.orderNo}
              </div>
              <div className={styles.insurerSyncLabel}>推送保險公司</div>
              <div className={styles.insurerSyncValue}>
                {insurerSyncSummary.insurerName}
              </div>
              <div className={styles.insurerSyncLabel}>保險經紀公司</div>
              <div className={styles.insurerSyncValue}>
                {insurerSyncSummary.brokerCompanyName}
              </div>
              <div className={styles.insurerSyncLabel}>保險經紀公司Code</div>
              <div className={styles.insurerSyncValue}>
                {insurerSyncSummary.brokerCompanyCode}
              </div>
              <div className={styles.insurerSyncLabel}>簽單業務代表</div>
              <div className={styles.insurerSyncValue}>
                {insurerSyncSummary.advisorName}
              </div>
              <div className={styles.insurerSyncLabel}>保司專屬簽單Code</div>
              <div
                className={[
                  styles.insurerSyncValue,
                  !insurerSyncSummary.hasAdvisorInsuranceCode
                    ? styles.insurerSyncMissingCodeValue
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {insurerSyncSummary.advisorInsuranceCode}
              </div>
              <div className={styles.insurerSyncLabel}>投保人</div>
              <div className={styles.insurerSyncValue}>
                {insurerSyncSummary.policyholderName}
              </div>
              <div className={styles.insurerSyncLabel}>投保基本計劃</div>
              <div className={styles.insurerSyncValue}>
                {insurerSyncSummary.productName}
              </div>
            </div>
            <p className={styles.insurerSyncWarning}>
              數據推送後，請憑簽單業務代表保司Code登錄保司系統，完成後續投保流程。
            </p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};
